/**
 * Group Management Tabs Component
 * Handles tab navigation and content rendering for different group views
 */

import {
  Badge,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { usePendingGroupRequestsCount } from '../../hooks/api/useGroupRequests';
import type { AppGroup } from '../../types/group';

import { GroupList } from './GroupList';
import { GroupRequestsList } from './GroupRequestsList';
import { MyGroupRequestsList } from './MyGroupRequestsList';

enum TabValue {
  ALL = 0,
  MY_GROUPS = 1,
  MY_REQUESTS = 2,
  LOCAL = 3,
  EXTERNAL = 4,
  PENDING_REQUESTS = 5,
}

interface GroupManagementTabsProps {
  hasGroupManagementPermission: boolean;
  allGroups: AppGroup[];
  myGroups: AppGroup[];
  activeTab: number;
  onTabChange: (tab: number) => void;
  onGroupClick: (group: AppGroup) => void;
  onManageMembers?: (group: AppGroup) => void;
  selectedOrgId?: string;
  /**
   * Whether to show pending requests tab (moderator/admin only)
   */
  showPendingRequestsTab?: boolean;
  /**
   * Callback when a request is processed (approved/rejected)
   */
  onRequestProcessed?: () => void;
}

export const GroupManagementTabs: React.FC<GroupManagementTabsProps> = ({
  hasGroupManagementPermission,
  allGroups,
  myGroups,
  activeTab,
  onTabChange,
  onGroupClick,
  onManageMembers,
  selectedOrgId,
  showPendingRequestsTab = false,
  onRequestProcessed,
}) => {
  const { t } = useTranslation();
  const { user } = usePermissions();
  // Multi-org feature - currently superadmin only (future: can.manageAllOrganizations)
  const isSuperadmin = user?.role === 'superadmin';
  // Open approvals, polled in the background so the tab lights up without a reload.
  const pendingCount = usePendingGroupRequestsCount(Boolean(showPendingRequestsTab));

  const getFilteredGroups = (filter: 'local' | 'external') => {
    return allGroups.filter(g =>
      filter === 'local' ? g.source === 'local' : g.source !== 'local'
    );
  };

  // Simple view for regular users - show My Groups and My Requests tabs
  if (!hasGroupManagementPermission || (isSuperadmin && !selectedOrgId)) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, value) => onTabChange(value)}>
            <Tab label={t('groups.tabs.myGroups')} />
            <Tab label={t('groups.tabs.myRequests')} />
          </Tabs>
        </Box>
        <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
          {activeTab === 0 && (
            <GroupList
              groups={myGroups}
              onGroupClick={onGroupClick}
              title=""
              showSource={true}
              emptyMessage={t('groups.emptyStates.contactAdminToJoin')}
            />
          )}
          {activeTab === 1 && (
            <MyGroupRequestsList onDataChange={onRequestProcessed} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, value) => onTabChange(value)}>
          <Tab label={t('groups.tabs.allGroups')} />
          <Tab label={t('groups.tabs.myGroups')} />
          <Tab label={t('groups.tabs.myRequests')} />
          <Tab label={t('groups.tabs.localGroups')} />
          <Tab label={t('groups.tabs.externalGroups')} />
          {showPendingRequestsTab && (
            <Tab
              label={
                <Badge
                  badgeContent={pendingCount}
                  color="warning"
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16, right: -8, top: -2 } }}
                >
                  {t('groups.tabs.pendingRequests')}
                </Badge>
              }
              sx={pendingCount > 0 ? { color: 'warning.dark', fontWeight: 600, overflow: 'visible' } : { overflow: 'visible' }}
            />
          )}
        </Tabs>
      </Box>

      <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === TabValue.ALL && (
          <GroupList
            groups={allGroups}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.allGroups')}
            showSource={true}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.MY_GROUPS && (
          <GroupList
            groups={myGroups}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.myGroups')}
            showSource={true}
            emptyMessage={t('groups.emptyStates.notMemberOfAnyGroups')}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.MY_REQUESTS && (
          <MyGroupRequestsList onDataChange={onRequestProcessed} />
        )}
        {activeTab === TabValue.LOCAL && (
          <GroupList
            groups={getFilteredGroups('local')}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.localGroups')}
            showSource={false}
            emptyMessage={t('groups.emptyStates.noLocalGroups')}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.EXTERNAL && (
          <GroupList
            groups={getFilteredGroups('external')}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.externalGroups')}
            showSource={true}
            emptyMessage={t('groups.emptyStates.noExternalGroups')}
            canManageGroups={false} // External groups cannot be managed
          />
        )}
        {activeTab === TabValue.PENDING_REQUESTS && showPendingRequestsTab && (
          <GroupRequestsList onRequestProcessed={onRequestProcessed} />
        )}
      </CardContent>
    </Card>
  );
};
